/**
 * roto-editor.js — the manual rotoscoping editor for video-bg-remover.
 *
 * Builds its own DOM inside a host element (so the blade stays thin) and lets
 * the user paint per-frame corrections on top of the RVM matte:
 *   • KEEP brush   — restore foreground the AI dropped
 *   • REMOVE brush — erase background the AI kept
 *   • ERASE        — undo manual paint back to the AI matte
 * Plus: undo/redo, zoom, onion-skinning, a frame timeline with keyframe ticks,
 * and "hold" propagation (a painted frame's correction holds across a range).
 *
 * It reads VbrMatteStore for frames and drives VbrRoto for the corrections.
 * Live painting stamps a working-resolution correction canvas at interactive
 * speed; the committed stroke list is the source of truth.
 *
 * Public API — window.VbrRotoEditor:
 *   mount(hostEl, t)      build DOM (t = label lookup fn)
 *   open(onDone)          enter the editor (onDone called when user finishes)
 *   destroy()
 */
(function (root) {
    'use strict';
    // window.__perPixel / window.__setText (timbrica-core.js) write a position or a text only when what is
    // shown changes. A page built before they existed can still be handed this file — it then writes the
    // plain way. Why: docs/why/primitives/per-pixel-writes.md.
    function pxWrite(e, p, v, u) { if (window.__perPixel) return window.__perPixel(e, p, v, u); e.style[p] = v + (u || '%'); }


    function el(tag, cls, html) {
        var e = document.createElement(tag);
        if (cls) e.className = cls;
        if (html != null) e.innerHTML = html;
        return e;
    }

    var host = null, T = function (k) { return k; };
    var dom = {};
    var onDoneCb = null;

    // editor state
    var st = {
        frame: 0, count: 0, fps: 30,
        tool: 'keep', size: 24, softness: 0.5, zoom: 1,
        onionPrev: false, onionNext: false,
        painting: false, pts: null, lastX: 0, lastY: 0,
        playing: false, playTimer: 0,
        curMatte: null, curMatteIdx: -1, live: null,
        rafPending: false,
    };

    /* ───────────────────────── DOM ───────────────────────── */

    function build() {
        var wrap = el('div', 'vbr-ed');

        // toolbar
        var bar = el('div', 'vbr-ed-bar');
        function toolBtn(tool, label) {
            var b = el('button', 'vbr-ed-tool' + (tool === st.tool ? ' active' : ''), label);
            b.dataset.tool = tool;
            b.addEventListener('click', function () { setTool(tool); });
            return b;
        }
        dom.toolKeep = toolBtn('keep', T('ed_keep'));
        dom.toolRemove = toolBtn('remove', T('ed_remove'));
        dom.toolErase = toolBtn('erase', T('ed_erase'));
        dom.undo = el('button', 'vbr-ed-icn', '&#8630;');
        dom.redo = el('button', 'vbr-ed-icn', '&#8631;');
        dom.undo.title = T('ed_undo'); dom.redo.title = T('ed_redo');
        dom.undo.addEventListener('click', doUndo);
        dom.redo.addEventListener('click', doRedo);
        dom.zoomOut = el('button', 'vbr-ed-icn', '&minus;');
        dom.zoomIn = el('button', 'vbr-ed-icn', '+');
        dom.zoomLbl = el('span', 'vbr-ed-zoom', '100%');
        dom.zoomOut.addEventListener('click', function () { setZoom(st.zoom - 0.5); });
        dom.zoomIn.addEventListener('click', function () { setZoom(st.zoom + 0.5); });
        dom.done = el('button', 'vbr-ed-done', T('ed_done'));
        dom.done.addEventListener('click', finish);

        bar.appendChild(dom.toolKeep);
        bar.appendChild(dom.toolRemove);
        bar.appendChild(dom.toolErase);
        bar.appendChild(el('span', 'vbr-ed-sep'));
        bar.appendChild(dom.undo);
        bar.appendChild(dom.redo);
        bar.appendChild(el('span', 'vbr-ed-sep'));
        bar.appendChild(dom.zoomOut);
        bar.appendChild(dom.zoomLbl);
        bar.appendChild(dom.zoomIn);
        bar.appendChild(dom.done);
        wrap.appendChild(bar);

        // body: stage + side
        var body = el('div', 'vbr-ed-body');
        dom.scroll = el('div', 'vbr-ed-scroll');
        dom.stage = el('div', 'vbr-ed-stage');
        dom.canvas = el('canvas', 'vbr-ed-canvas');
        dom.cursor = el('div', 'vbr-ed-cursor');
        dom.stage.appendChild(dom.canvas);
        dom.stage.appendChild(dom.cursor);
        dom.scroll.appendChild(dom.stage);
        body.appendChild(dom.scroll);

        var side = el('div', 'vbr-ed-side');
        side.appendChild(sliderRow(T('ed_brush_size'), 5, 80, st.size, 'px', function (v) {
            st.size = v; updateCursor();
        }, function (r) { dom.sizeRow = r; }));
        side.appendChild(sliderRow(T('ed_softness'), 0, 100, st.softness * 100, '%', function (v) {
            st.softness = v / 100;
        }));
        var onion = el('div', 'vbr-ed-group');
        onion.appendChild(el('div', 'vbr-ed-grouplabel', T('ed_onion')));
        onion.appendChild(checkRow(T('ed_onion_prev'), function (on) { st.onionPrev = on; render(); }));
        onion.appendChild(checkRow(T('ed_onion_next'), function (on) { st.onionNext = on; render(); }));
        side.appendChild(onion);

        var hold = el('div', 'vbr-ed-group');
        hold.appendChild(el('div', 'vbr-ed-grouplabel', T('ed_hold')));
        var holdRow = el('div', 'vbr-ed-holdrow');
        dom.holdInput = el('input', 'vbr-ed-holdinput');
        dom.holdInput.type = 'number'; dom.holdInput.min = '1';
        dom.holdBtn = el('button', 'vbr-ed-holdbtn', T('ed_hold_apply'));
        dom.holdBtn.addEventListener('click', applyHold);
        holdRow.appendChild(dom.holdInput);
        holdRow.appendChild(dom.holdBtn);
        hold.appendChild(holdRow);
        hold.appendChild(el('div', 'vbr-ed-hint', T('ed_hold_hint')));
        side.appendChild(hold);

        dom.clearFrame = el('button', 'vbr-ed-clearbtn', T('ed_clear_frame'));
        dom.clearFrame.addEventListener('click', function () {
            VbrRoto.clearFrame(st.frame);
            loadLive(); render(); refreshTrack(); refreshUndo();
        });
        side.appendChild(dom.clearFrame);
        body.appendChild(side);
        wrap.appendChild(body);

        // timeline
        var tl = el('div', 'vbr-ed-timeline');
        dom.prevBtn = el('button', 'vbr-ed-icn', '&#9664;');
        dom.playBtn = el('button', 'vbr-ed-icn', '&#9654;');
        dom.nextBtn = el('button', 'vbr-ed-icn', '&#9654;|');
        dom.prevBtn.addEventListener('click', function () { goto(st.frame - 1); });
        dom.nextBtn.addEventListener('click', function () { goto(st.frame + 1); });
        dom.playBtn.addEventListener('click', togglePlay);
        dom.counter = el('span', 'vbr-ed-counter', '1 / 1');
        dom.track = el('div', 'vbr-ed-track');
        dom.playhead = el('div', 'vbr-ed-playhead');
        dom.track.appendChild(dom.playhead);
        dom.track.addEventListener('pointerdown', function (e) {
            var rect = dom.track.getBoundingClientRect();
            var p = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            goto(Math.round(p * (st.count - 1)));
        });
        tl.appendChild(dom.prevBtn);
        tl.appendChild(dom.playBtn);
        tl.appendChild(dom.nextBtn);
        tl.appendChild(dom.counter);
        tl.appendChild(dom.track);
        wrap.appendChild(tl);

        host.appendChild(wrap);
        wireCanvas();
        wireKeyboard();
    }

    function sliderRow(label, min, max, val, unit, onChange, refCb) {
        var row = el('div', 'vbr-ed-group');
        var head = el('div', 'vbr-ed-grouplabel');
        var lbl = document.createTextNode(label + ' ');
        var valSpan = el('span', 'vbr-ed-val', Math.round(val) + unit);
        head.appendChild(lbl); head.appendChild(valSpan);
        var input = el('input', 'vbr-ed-slider');
        input.type = 'range'; input.min = min; input.max = max; input.value = val;
        input.addEventListener('input', function () {
            valSpan.textContent = Math.round(input.value) + unit;
            onChange(parseFloat(input.value));
        });
        row.appendChild(head); row.appendChild(input);
        if (refCb) refCb(row);
        return row;
    }

    function checkRow(label, onChange) {
        var row = el('label', 'vbr-ed-check');
        var cb = el('input');
        cb.type = 'checkbox';
        cb.addEventListener('change', function () { onChange(cb.checked); });
        row.appendChild(cb);
        row.appendChild(document.createTextNode(' ' + label));
        return row;
    }

    /* ───────────────────────── tools / state ───────────────────────── */

    function setTool(tool) {
        st.tool = tool;
        [dom.toolKeep, dom.toolRemove, dom.toolErase].forEach(function (b) {
            b.classList.toggle('active', b.dataset.tool === tool);
        });
        dom.cursor.classList.toggle('remove', tool === 'remove');
    }

    function setZoom(z) {
        st.zoom = Math.max(1, Math.min(5, z));
        dom.zoomLbl.textContent = Math.round(st.zoom * 100) + '%';
        layoutCanvas();
    }

    function refreshUndo() {
        dom.undo.disabled = !VbrRoto.canUndo();
        dom.redo.disabled = !VbrRoto.canRedo();
    }

    function doUndo() {
        var f = VbrRoto.undo();
        if (f != null) { st.frame = Math.max(0, Math.min(st.count - 1, f)); }
        loadLive(); render(); refreshTrack(); refreshUndo(); syncCounter();
    }

    function doRedo() {
        var f = VbrRoto.redo();
        if (f != null) { st.frame = Math.max(0, Math.min(st.count - 1, f)); }
        loadLive(); render(); refreshTrack(); refreshUndo(); syncCounter();
    }

    function applyHold() {
        var end = parseInt(dom.holdInput.value, 10);
        if (!end || end <= st.frame + 1) return;
        VbrRoto.setHold(st.frame, Math.min(st.count - 1, end - 1));
        refreshTrack(); refreshUndo();
        try { window.__toast && window.__toast(T('ed_hold_done'), 'success'); } catch (_) {}
    }

    /* ───────────────────────── canvas layout + render ───────────────────────── */

    function layoutCanvas() {
        var meta = VbrMatteStore.meta();
        if (!meta) return;
        dom.canvas.width = meta.width;
        dom.canvas.height = meta.height;
        var avail = dom.scroll.clientWidth - 24;
        var fitW = Math.min(avail, meta.width);
        var dispW = fitW * st.zoom;
        dom.canvas.style.width = dispW + 'px';
        dom.canvas.style.height = (dispW * meta.height / meta.width) + 'px';
        updateCursor();
    }

    function render() {
        if (st.rafPending) return;
        st.rafPending = true;
        requestAnimationFrame(function () {
            st.rafPending = false;
            drawNow();
        });
    }

    function drawNow() {
        var ctx = dom.canvas.getContext('2d');
        var w = dom.canvas.width, h = dom.canvas.height;
        ctx.clearRect(0, 0, w, h);
        if (!st.curMatte) return;

        if (st.onionPrev && onionCache.prev) drawOnion(ctx, onionCache.prev, 'rgba(96,165,250,0.35)');
        if (st.onionNext && onionCache.next) drawOnion(ctx, onionCache.next, 'rgba(244,114,182,0.35)');

        var corrected = VbrRoto.applyCorrectionCanvas(st.curMatte, st.live);
        ctx.drawImage(corrected, 0, 0, w, h);
    }

    var onionCache = { prev: null, next: null, prevIdx: -1, nextIdx: -1 };

    function drawOnion(ctx, matte, tint) {
        // Faint silhouette of a neighbouring frame's matte.
        var t = document.createElement('canvas');
        t.width = matte.width; t.height = matte.height;
        var tc = t.getContext('2d');
        tc.drawImage(matte, 0, 0);
        tc.globalCompositeOperation = 'source-in';
        tc.fillStyle = tint;
        tc.fillRect(0, 0, t.width, t.height);
        ctx.drawImage(t, 0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    async function loadOnion() {
        onionCache.prev = onionCache.next = null;
        if (st.onionPrev && st.frame > 0) onionCache.prev = await VbrMatteStore.get(st.frame - 1);
        if (st.onionNext && st.frame < st.count - 1) onionCache.next = await VbrMatteStore.get(st.frame + 1);
    }

    /* ───────────────────────── frame navigation ───────────────────────── */

    function loadLive() {
        var existing = VbrRoto.rasterize(st.frame);
        var live = VbrRoto.blankCorrection();
        if (existing) live.getContext('2d').drawImage(existing, 0, 0);
        st.live = live;
    }

    async function goto(idx) {
        idx = Math.max(0, Math.min(st.count - 1, idx | 0));
        st.frame = idx;
        loadLive();
        syncCounter();
        st.curMatte = await VbrMatteStore.get(idx);
        st.curMatteIdx = idx;
        await loadOnion();
        render();
    }

    function syncCounter() {
        dom.counter.textContent = (st.frame + 1) + ' / ' + st.count;
        pxWrite(dom.playhead, 'left', st.count > 1 ? st.frame / (st.count - 1) * 100 : 0);
        dom.holdInput.placeholder = Math.min(st.count, st.frame + 10);
    }

    function refreshTrack() {
        // keyframe ticks + hold-range bands
        Array.prototype.slice.call(dom.track.querySelectorAll('.vbr-ed-tick,.vbr-ed-band'))
            .forEach(function (n) { n.remove(); });
        var ranges = VbrRoto.ranges();
        ranges.forEach(function (r) {
            var band = el('div', 'vbr-ed-band');
            band.style.left = (r.start / (st.count - 1) * 100) + '%';
            band.style.width = ((r.end - r.start) / (st.count - 1) * 100) + '%';
            dom.track.appendChild(band);
        });
        VbrRoto.keyframes().forEach(function (f) {
            var tick = el('div', 'vbr-ed-tick');
            tick.style.left = (st.count > 1 ? f / (st.count - 1) * 100 : 0) + '%';
            dom.track.appendChild(tick);
        });
    }

    function togglePlay() {
        st.playing = !st.playing;
        dom.playBtn.innerHTML = st.playing ? '&#10073;&#10073;' : '&#9654;';
        if (st.playing) {
            var step = function () {
                if (!st.playing) return;
                goto(st.frame >= st.count - 1 ? 0 : st.frame + 1);
                st.playTimer = setTimeout(step, 1000 / Math.min(st.fps, 15));
            };
            step();
        } else {
            clearTimeout(st.playTimer);
        }
    }

    /* ───────────────────────── painting ───────────────────────── */

    function canvasCoords(e) {
        var rect = dom.canvas.getBoundingClientRect();
        var meta = VbrMatteStore.meta();
        var mx = (e.clientX - rect.left) / rect.width * meta.width;
        var my = (e.clientY - rect.top) / rect.height * meta.height;
        // map matte-resolution coords → working-resolution coords
        var k = VbrRoto._work.w / meta.width;
        return { x: mx * k, y: my * k };
    }

    function wireCanvas() {
        dom.canvas.addEventListener('pointerdown', function (e) {
            if (st.playing) togglePlay();
            st.painting = true;
            var c = canvasCoords(e);
            st.pts = [c.x, c.y];
            st.lastX = c.x; st.lastY = c.y;
            dom.canvas.setPointerCapture(e.pointerId);
            stampSegment(c.x, c.y, c.x, c.y);
        });
        dom.canvas.addEventListener('pointermove', function (e) {
            moveCursor(e);
            if (!st.painting) return;
            var c = canvasCoords(e);
            st.pts.push(c.x, c.y);
            stampSegment(st.lastX, st.lastY, c.x, c.y);
            st.lastX = c.x; st.lastY = c.y;
        });
        function endStroke() {
            if (!st.painting) return;
            st.painting = false;
            if (st.pts && st.pts.length >= 2) {
                VbrRoto.addStroke(st.frame, {
                    mode: st.tool, size: st.size, softness: st.softness,
                    pts: Float32Array.from(st.pts),
                });
                refreshTrack(); refreshUndo();
            }
            st.pts = null;
        }
        dom.canvas.addEventListener('pointerup', endStroke);
        dom.canvas.addEventListener('pointercancel', endStroke);
        dom.canvas.addEventListener('pointerleave', function () { dom.cursor.style.display = 'none'; });
        dom.canvas.addEventListener('pointerenter', function () { dom.cursor.style.display = 'block'; });
    }

    function stampSegment(x0, y0, x1, y1) {
        VbrRoto.stampStroke(st.live.getContext('2d'), {
            mode: st.tool, size: st.size, softness: st.softness,
            pts: [x0, y0, x1, y1],
        });
        render();
    }

    function moveCursor(e) {
        var rect = dom.canvas.getBoundingClientRect();
        st.lastClientX = e.clientX; st.lastClientY = e.clientY;
        dom.cursor.style.left = (e.clientX - dom.stage.getBoundingClientRect().left) + 'px';
        dom.cursor.style.top = (e.clientY - dom.stage.getBoundingClientRect().top) + 'px';
        updateCursor();
    }

    function updateCursor() {
        var rect = dom.canvas.getBoundingClientRect();
        var meta = VbrMatteStore.meta();
        if (!meta || !rect.width) return;
        // brush size is working-res px → display px
        var dispPerWork = rect.width / VbrRoto._work.w;
        var d = st.size * dispPerWork;
        dom.cursor.style.width = d + 'px';
        dom.cursor.style.height = d + 'px';
    }

    /* ───────────────────────── keyboard ───────────────────────── */

    function wireKeyboard() {
        document.addEventListener('keydown', function (e) {
            if (!isOpen()) return;
            if (e.target && /INPUT|TEXTAREA/.test(e.target.tagName)) return;
            var kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
            var k = kl || String(e.key || '').toLowerCase();
            // «[ ] , .» by the key's place: the Russian layout prints «х ъ б ю» there.
            var ks = window.__keySign ? window.__keySign(e) : e.key;
            if ((e.ctrlKey || e.metaKey) && k === 'z') { e.preventDefault(); e.shiftKey ? doRedo() : doUndo(); }
            else if ((e.ctrlKey || e.metaKey) && k === 'y') { e.preventDefault(); doRedo(); }
            else if (ks ? (window.__keyChord ? window.__keyChord(e) : (e.ctrlKey || e.metaKey)) : (kl && (e.ctrlKey || e.metaKey || e.altKey))) return;
            else if (k === 'b') setTool('keep');
            else if (k === 'r') setTool('remove');
            else if (k === 'e') setTool('erase');
            else if (ks === '[') { st.size = Math.max(5, st.size - 4); syncSizeSlider(); updateCursor(); }
            else if (ks === ']') { st.size = Math.min(80, st.size + 4); syncSizeSlider(); updateCursor(); }
            else if (ks === ',') goto(st.frame - 1);
            else if (ks === '.') goto(st.frame + 1);
            else if (k === ' ') { e.preventDefault(); togglePlay(); }
        });
    }

    function syncSizeSlider() {
        if (!dom.sizeRow) return;
        var input = dom.sizeRow.querySelector('input');
        var val = dom.sizeRow.querySelector('.vbr-ed-val');
        if (input) input.value = st.size;
        if (val) val.textContent = Math.round(st.size) + 'px';
    }

    /* ───────────────────────── lifecycle ───────────────────────── */

    function isOpen() {
        return host && host.offsetParent !== null;
    }

    function mount(hostEl, t) {
        host = hostEl;
        if (t) T = t;
        host.innerHTML = '';
        build();
    }

    async function open(onDone) {
        onDoneCb = onDone || null;
        var meta = VbrMatteStore.meta();
        st.count = VbrMatteStore.count();
        st.fps = meta ? meta.fps : 30;
        st.frame = 0;
        setTool('keep');
        setZoom(1);
        layoutCanvas();
        refreshTrack();
        refreshUndo();
        await goto(0);
    }

    function finish() {
        if (st.playing) togglePlay();
        if (onDoneCb) onDoneCb();
    }

    function destroy() {
        if (st.playing) { st.playing = false; clearTimeout(st.playTimer); }
        if (host) host.innerHTML = '';
    }

    root.VbrRotoEditor = {
        mount: mount,
        open: open,
        destroy: destroy,
    };
})(typeof window !== 'undefined' ? window : globalThis);
